import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils";
import SectionJumpBar from "@/components/shared/SectionJumpBar";

// Every toolbar button is 32px tall. h-8 with py-0, because `text-[13px]` and
// friends set a font size but no line height, which left buttons on the same
// row 5px apart in height.
export const TOOLBAR_BUTTON = "h-8 shrink-0 rounded-lg px-4 py-0 text-xs disabled:opacity-50";

const CHIP_TONE = {
  amber: "border-tone-amber/30 bg-tone-amber-soft text-tone-amber",
  rose: "border-tone-rose/30 bg-tone-rose-soft text-tone-rose",
  emerald: "border-tone-emerald/30 bg-tone-emerald-soft text-tone-emerald",
  slate: "border-border-primary bg-background-primary text-text-secondary",
};

/** The one-line "can I save this yet?" answer on a form pane's action bar. */
export const ReviewStatusChip = ({ label, tone = "slate", className = "" }) => (
  <span className={cn("inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-medium", CHIP_TONE[tone] ?? CHIP_TONE.slate, className)}>{label}</span>
);

/**
 * The screen shape every resume review page uses: a 32px toolbar, then a form
 * pane and a resume pane side by side, each scrolling on its own.
 *
 * The page fills the height the admin layout leaves and never scrolls itself,
 * so the toolbar and the pane action bars stay on screen however far the
 * reviewer has scrolled. Below lg the panes stack at 70vh and the page scrolls
 * between them — side-by-side reading needs the width.
 *
 * @param {() => void} [onBack] - renders the back button when given
 * @param {boolean} [backDisabled] - hold the way out while a submit is in flight
 * @param {ReactNode} [actions] - toolbar right side: page-level buttons
 * @param {ReactNode} [belowToolbar] - full-width row under the toolbar (a step rail)
 * @param {ReactNode} left - the form pane; usually a <ReviewFormPane>
 * @param {ReactNode} right - the resume pane; usually a <ResumePreview>
 */
export const SplitReviewLayout = ({ onBack, backLabel = "Back", backDisabled = false, actions, belowToolbar, left, right }) => (
  <div className="flex flex-col gap-2 lg:min-h-0 lg:flex-1">
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-2">
      {onBack && (
        <Button
          onClick={onBack}
          disabled={backDisabled}
          className="flex h-8 w-fit shrink-0 items-center gap-1 rounded-lg border border-border-primary bg-background-tertiary px-3 py-0 text-xs text-text-secondary hover:text-text-primary disabled:cursor-not-allowed disabled:opacity-50"
        >
          <ArrowLeft size={14} /> {backLabel}
        </Button>
      )}

      {actions && <div className="ml-auto flex items-center gap-3">{actions}</div>}
    </div>

    {belowToolbar}

    <div className="flex flex-col gap-3 px-2 pb-2 lg:min-h-0 lg:flex-1 lg:flex-row">
      {/* No resume to show (still extracting) — the form pane takes the width
          rather than sitting next to an empty box. */}
      <div className={cn("h-[70vh] w-full lg:h-full lg:min-h-0", right ? "lg:w-1/2" : "lg:w-full")}>{left}</div>
      {right && <div className="h-[70vh] w-full overflow-hidden lg:h-full lg:min-h-0 lg:w-1/2">{right}</div>}
    </div>
  </div>
);

/**
 * The left pane: a jump bar, the scrolling form, and an action bar pinned
 * under it. The action bar is a flex row after the scroll area rather than a
 * sticky element inside it, so the save button is on screen at every scroll
 * position.
 *
 * @param {Array<{key: string, label: string}>} [sections] - jump bar targets
 * @param {(key: string) => void} [onSectionClick]
 * @param {ReactNode} [headerRight] - trailing item on the jump bar row
 * @param {ReactNode} [footer] - the action bar; omitted when the pane has no actions
 */
export const ReviewFormPane = ({ sections = [], onSectionClick, headerRight, footer, className = "", children }) => {
  const hasHeader = (onSectionClick && sections.length > 0) || headerRight;

  return (
    <div className={cn("flex h-full flex-col rounded-xl border border-border-primary bg-background-primary", className)}>
      {hasHeader && (
        <div className="flex shrink-0 items-center gap-2 rounded-t-xl border-b border-border-primary bg-background-secondary px-3 py-1.5">
          <SectionJumpBar sections={sections} onSectionClick={onSectionClick} className="min-w-0 flex-1" />
          {headerRight}
        </div>
      )}

      <div className="custom-scrollbar min-h-0 flex-1 space-y-2 overflow-y-auto p-2">{children}</div>

      {footer && <div className="shrink-0 rounded-b-xl border-t border-border-primary bg-background-secondary px-3 py-2">{footer}</div>}
    </div>
  );
};
